// composables/useShellRoute.js
// Shell-level F7 router subscription. Owns currentRoute ref + the routeChange
// listener with onScopeDispose cleanup so layouts cannot drift on listener
// lifecycle (Playbook Section 5 row 9 / row 14, audit HIGH-08/09).
//
// Why this is more involved than a single f7ready callback
// ─────────────────────────────────────────────────────────────────────────────
// `f7ready(cb)` resolves SYNCHRONOUSLY when `f7.initialized === true`
// (framework7-vue/shared/f7.js: f7ready). By the time AppLayout mounts,
// f7 is already initialized (App.vue gated isReady on f7's own f7ready),
// so `f7ready(cb)` here fires immediately during AppLayout's <script setup>
// — BEFORE AppLayout's template renders and BEFORE <f7-view> mounts. At
// that moment `f7.views.main` is still null (it gets assigned by
// view-class.js when <f7-view>'s onMounted runs, which happens AFTER the
// parent's script setup). A naive `f7.views.main?.router` read returns
// null → bail-out → the routeChange listener is NEVER attached → the
// `currentRoute` ref stays null forever → `showTabbar` stays false → the
// bottom tabbar renders display:none for the entire session.
//
// Fix: when the main view's router isn't ready yet, listen for F7's
// `routerInit` event (router-class.js: `router.emit('local::init routerInit', router)`
// at the end of `router.init()`, AFTER `router.currentRoute` is assigned).
// This guarantees we get a populated router and a valid initial route in
// one shot, before any user-driven navigation can fire.

import { f7ready } from 'framework7-vue';
import { onScopeDispose, ref } from 'vue';

export function useShellRoute() {
  const currentRoute = ref(null);
  let attachedRouter = null;
  // Guards against the race where the consuming scope disposes before the
  // async f7ready / routerInit callback resolves — without this, the
  // listener would attach after onScopeDispose has already run and leak.
  let disposed = false;

  const handleRouteChange = (newRoute) => {
    currentRoute.value = newRoute;
  };

  const attachToRouter = (router) => {
    if (!router || disposed || attachedRouter) return;
    currentRoute.value = router.currentRoute;
    router.on('routeChange', handleRouteChange);
    attachedRouter = router;
  };

  f7ready((f7) => {
    if (disposed) return;

    const mainRouter = f7.views.main?.router;
    if (mainRouter) {
      // Cold-boot fast path: view + router already exist (rare in practice
      // because of the script-setup-before-mount ordering described above,
      // but possible for late-mounting consumers).
      attachToRouter(mainRouter);
      return;
    }

    // routerInit fires from router-class.js init() AFTER router.currentRoute
    // is set, so we get a valid initial route AND attach our routeChange
    // listener atomically. We filter to the main view's router only; F7
    // may emit routerInit for non-main views (e.g. nested view stacks).
    const onRouterInit = (router) => {
      if (disposed) return;
      if (router?.view?.main !== true) return;
      f7.off('routerInit', onRouterInit);
      attachToRouter(router);
    };
    f7.on('routerInit', onRouterInit);

    // Belt-and-braces: also clean up the routerInit listener on dispose
    // even if it never fired (e.g. consumer unmounted before view init).
    onScopeDispose(() => {
      f7.off('routerInit', onRouterInit);
    });
  });

  onScopeDispose(() => {
    disposed = true;
    attachedRouter?.off('routeChange', handleRouteChange);
    attachedRouter = null;
  });

  return { currentRoute };
}
